/**
 * cart-add-merge.js
 *
 * Tier-aware cart add for bundled offer items.
 *
 * window.CartBundleAdd.smartAdd(items)
 *
 * Before adding bundled items, checks the cart for an existing "main" item
 * from the same tier (detected via the _bundleId prefix, e.g. "explorer_<ts>").
 *
 *  - Same tier and same private bundle properties already in cart → increment
 *    only the main item quantity, leave gifts completely untouched.
 *  - Same tier from a different offer → add a separate bundle. Offer pages
 *    reconcile concurrent bundles by timestamp after the add.
 *  - New tier (or empty cart)  → add all items (main + gifts) as normal.
 *
 * After any successful mutation, publishes theme.pubsub cartUpdate WITH sections
 * HTML (same payload as ProductForm) so CartItems updates the drawer DOM, then
 * calls cartDrawer.show() directly — the exact same path as the native theme.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Network helpers
  // ---------------------------------------------------------------------------

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(async function (r) {
      if (!r.ok) throw await failedResponseError(r, 'cart_verify', '/cart.js');
      return r.json();
    });
  }

  /**
   * Collect the section IDs that CartDrawer (and other listeners) need bundled
   * into the /cart/change.js response — mirrors what CartItems.updateQuantity does.
   * Returns an array of section ID strings (may be empty if no listeners).
   */
  function collectBundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function updateCartCountBadge(count) {
    var parsed = Number(count);
    var safeCount = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.innerText = String(safeCount);
      el.hidden = safeCount === 0;
    });
  }

  /**
   * The FIRST `[aria-controls="CartDrawer"]` in the document is often the
   * overlay inside `<cart-drawer>` (see cart-drawer.liquid), NOT the header
   * cart link. Passing that to `show()` breaks opening — the real icon works.
   * Prefer the actual cart triggers (header, mobile dock), then fall back.
   */
  function getCartDrawerToggleControl() {
    return (
      document.querySelector('a.cart-drawer-button[aria-controls="CartDrawer"]') ||
      document.querySelector('a.dock__item[aria-controls="CartDrawer"]') ||
      document.querySelector('a[href*="cart"][aria-controls="CartDrawer"]') ||
      document.querySelector('button[aria-controls="CartDrawer"]') ||
      document.querySelector('[aria-controls="CartDrawer"]')
    );
  }

  /**
   * Same path as clicking the real cart icon: `onButtonClick` →
   * `this.open ? hide() : show(event.currentTarget)`.
   * Uses the correct control element + `.click()` so behavior matches the user.
   */
  async function openCartDrawerLikeHeader() {
    var cartDrawer = getPreferredCartDrawer();
    var ctrl = getCartDrawerToggleControl();
    if (!cartDrawer) {
      return;
    }
    if (!ctrl) {
      if (typeof cartDrawer.show === 'function') cartDrawer.show(null, true);
      return;
    }

    if (typeof window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__ !== 'undefined' && window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__) {
      window.__TASTE_PROFILE_SUPPRESS_CART_DRAWER__ = false;
    }

    if (cartDrawer.style && cartDrawer.style.display === 'none') {
      cartDrawer.style.display = '';
    }

    if (cartDrawer.open) {
      try {
        if (typeof cartDrawer.hide === 'function') {
          await Promise.race([
            cartDrawer.hide(),
            new Promise(function (resolve) { setTimeout(resolve, 600); }),
          ]);
        } else {
          cartDrawer.removeAttribute('open');
        }
      } catch (e) {
        cartDrawer.removeAttribute('open');
      }
    }

    ctrl.click();
  }

  function getPreferredCartDrawer() {
    var byId = document.getElementById('CartDrawer');
    if (byId && byId.tagName && byId.tagName.toLowerCase() === 'cart-drawer') return byId;

    var all = Array.prototype.slice.call(document.querySelectorAll('cart-drawer'));
    if (!all.length) return null;

    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden) return all[i];
    }
    return all[0];
  }

  /**
   * POST /cart/change.js with sections bundled so the response includes
   * pre-rendered section HTML — same as the theme's CartItems.updateQuantity.
   */
  function cartChange(key, quantity) {
    var sections = collectBundledSections();
    var body = { id: key, quantity: quantity };
    if (sections.length) body.sections = sections;

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async function (r) {
      if (!r.ok) throw await failedResponseError(r, 'cart_add', '/cart/change.js');
      return r.json();
    });
  }

  // ---------------------------------------------------------------------------
  // Cart UI notification
  // ---------------------------------------------------------------------------

  /**
   * After a successful cart mutation, update the UI and open the cart drawer.
   *
   * Mirrors what ProductForm does after /cart/add.js:
   *   1. Publish theme.pubsub cartUpdate WITH sections so CartItems can update
   *      the drawer DOM without throwing (it needs sections[sectionId] HTML).
   *   2. Update cart-count badge directly as a safe fallback.
   *   3. Call cartDrawer.show() — the native show path that handles body classes,
   *      animations, and focus trapping.
   *
   * @param {Object} parsedState  Response from /cart/change.js or /cart/add.js
   *                              (may include parsedState.sections if bundled).
   */
  async function notifyCartUI(parsedState, extra) {
    // Step 1: publish cartUpdate WITH sections so CartItems.onCartUpdate can
    // render the drawer HTML correctly (it reads event.cart.sections[sectionId]).
    // Isolated in try/catch because pubsub.publish has no internal error handling.
    try {
      if (
        typeof window.theme !== 'undefined' &&
        window.theme.pubsub &&
        typeof window.theme.pubsub.publish === 'function' &&
        window.theme.pubsub.PUB_SUB_EVENTS &&
        window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate &&
        parsedState
      ) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: extra && extra.productVariantId != null ? extra.productVariantId : undefined,
          cart: parsedState,
        });
      }
    } catch (e) {}

    // Step 2: update cart-count badge directly (safe fallback if pubsub failed).
    if (parsedState) {
      updateCartCountBadge(parsedState.item_count);
    }

    // Step 3: refresh drawer HTML only (no open:true — it races async show() with
    // our toggle click and used the wrong aria-controls target before). CartItems
    // already updated from pubsub; this keeps section markup in sync.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

    // Step 4: open using the REAL cart icon (not overlay) — see getCartDrawerToggleControl.
    await new Promise(function (resolve) { setTimeout(resolve, 300); });
    await openCartDrawerLikeHeader();
  }

  // ---------------------------------------------------------------------------
  // Tier helpers
  // ---------------------------------------------------------------------------

  function getTierFromBundleId(bundleId) {
    if (!bundleId || typeof bundleId !== 'string') return '';
    return bundleId.split('_')[0];
  }

  function findExistingTierMain(cartItems, tier, signature) {
    if (!tier) return null;
    for (var i = 0; i < cartItems.length; i++) {
      var li = cartItems[i];
      var props = li.properties || {};
      var bId = typeof props._bundleId === 'string' ? props._bundleId : '';
      var role = typeof props._role === 'string' ? props._role.toLowerCase() : '';
      if (
        role === 'main' &&
        getTierFromBundleId(bId) === tier &&
        bundleSignature(li) === signature
      ) return li;
    }
    return null;
  }

  // A tier is reused by the PDP, milestone campaigns and seasonal offers. Merging
  // across those programs keeps the old main line's attribution and pinned-bottle
  // properties, so the new offer can charge successfully but fulfill the wrong box.
  // Ignore only identity fields; every other private property defines the bundle.
  function bundleSignature(item) {
    var props = (item && item.properties) || {};
    return Object.keys(props)
      .filter(function (key) {
        return key.charAt(0) === '_' && key !== '_bundleId' && key !== '_role';
      })
      .sort()
      .map(function (key) { return key + '=' + String(props[key]); })
      .join('&');
  }

  /**
   * Reads a failed /cart/add.js (or /cart/change.js) response and returns the
   * message a shopper should see, preferring Shopify's own error body
   * ("The product 'X' is already sold out.") over anything we make up.
   *
   * A body that is not JSON is a page, not a cart error. Shopify's bot check
   * answers /cart/add.js with a 429 and its "Verifying your connection..."
   * HTML (2026-09-02, a PDP, iOS Chrome). A fetch can never pass that check
   * and a page load does, so the only useful instruction is to refresh. The
   * same instruction covers any other HTML error page. The status code and
   * body still reach #dev-alerts through cart-failure-reporter.js's fetch
   * intercept, which sees the raw response, so keeping them off the shopper's
   * screen loses nothing. Before this the fallback was the developer string
   * '[CartBundleAdd] /cart/add.js 429', and that is what the shopper read.
   *
   * The return value is shopper-facing (the PDP buy box renders err.message
   * verbatim), so it stays a clean sentence with no classifier markers.
   * Because cart-failure-reporter.js cannot regex-classify friendly text,
   * failedResponseError() below attaches stage, http_status and cart_endpoint
   * to the thrown Error, and the offer-page reporters forward those instead
   * of inferring them from the message.
   */
  async function describeFailedResponse(res) {
    var text = '';
    try {
      text = await res.text();
      var body = JSON.parse(text);
      if (body && (body.message || body.description)) {
        return body.message || body.description;
      }
    } catch (e) {}
    if (/verifying your connection/i.test(text)) {
      return 'We need to verify your connection. Please refresh the page and try again.';
    }
    return "We couldn't add that to your cart just now. Please refresh the page and try again.";
  }

  /**
   * Builds the Error every failed cart call throws: shopper sentence as the
   * message, and the facts the reporters need (stage, http_status,
   * cart_endpoint) as properties, since the message no longer carries them.
   */
  async function failedResponseError(res, stage, endpoint, reportToken) {
    var err = new Error(await describeFailedResponse(res));
    err.stage = stage;
    err.http_status = res.status;
    err.cart_endpoint = endpoint;
    // The same token rides on the fetch (cart-failure-reporter.js reads it off the
    // init) and on this error, so a caller that reports the failure and the
    // reporter's own interceptor count as one report, not two.
    if (reportToken) err._reportToken = reportToken;
    return err;
  }

  // ---------------------------------------------------------------------------
  // smartAdd
  // ---------------------------------------------------------------------------

  /**
   * @param {Array} items  Cart items with properties: { _bundleId, _role }.
   * @param {{ reportToken?: object }} [opts]  A caller that wraps this call (a deadline,
   *   a retry) passes its own token so the error it reports on a wrapper failure carries
   *   the same token as the fetch, and the reporter still counts one failure.
   */
  async function smartAdd(items, opts) {
    // One token per add: see failedResponseError.
    var reportToken = (opts && opts.reportToken) || {};
    var mainItem = null;
    for (var i = 0; i < items.length; i++) {
      if ((items[i].properties || {})._role === 'main') {
        mainItem = items[i];
        break;
      }
    }

    // No recognised main item — add everything as-is.
    if (!mainItem) {
      var sections = collectBundledSections();
      var addBody = { items: items };
      if (sections.length) addBody.sections = sections;
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(addBody),
        wlReportToken: reportToken,
      });
      if (!res.ok) throw await failedResponseError(res, 'cart_add', '/cart/add.js', reportToken);
      var parsedAdd = await res.json();
      // /cart/add.js answers with the added items, not the cart: without a real cart the badge
      // read item_count as undefined (and hid), and the theme's add listeners got no variant.
      // Read the cart once and publish it the way the product form does (2026-09-11).
      var cartAfterAdd = null;
      try { cartAfterAdd = await fetchCart(); } catch (e) { cartAfterAdd = null; }
      var state = cartAfterAdd ? Object.assign({}, cartAfterAdd, { sections: parsedAdd && parsedAdd.sections }) : parsedAdd;
      await notifyCartUI(state, { productVariantId: items[0] && items[0].id });
      return res;
    }

    var bundleId = (mainItem.properties || {})._bundleId || '';
    var tier = getTierFromBundleId(bundleId);

    var cart = await fetchCart();
    var existingMain = findExistingTierMain(
      cart.items || [],
      tier,
      bundleSignature(mainItem)
    );

    if (existingMain) {
      // Same tier and same program: bump main quantity only, gifts stay untouched.
      var addQty = Number(mainItem.quantity) || 1;
      var newQty = existingMain.quantity + addQty;
      var parsedChange = await cartChange(existingMain.key, newQty);
      await notifyCartUI(parsedChange);
      return new Response(JSON.stringify({ updated: true, tier: tier, quantity: newQty }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    // New tier or empty cart: add everything (main + gifts).
    var bundleSections = collectBundledSections();
    var newBundleBody = { items: items };
    if (bundleSections.length) newBundleBody.sections = bundleSections;
    var addRes = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(newBundleBody),
      wlReportToken: reportToken,
    });
    if (!addRes.ok) throw await failedResponseError(addRes, 'cart_add', '/cart/add.js', reportToken);
    var parsedNew = await addRes.json();
    await notifyCartUI(parsedNew);
    return addRes;
  }

  // ---------------------------------------------------------------------------
  // Toast — a shared, minimal notification for cart failures that have a
  // specific, shopper-relevant reason (a purchase-limit rejection, a Shopify
  // cart-validation message) so they are never silently dropped. Not tied to
  // smartAdd's own callers: assets/cart.js's quantity stepper uses it too, since
  // this file is already loaded globally (layout/theme.liquid) ahead of it.
  // ---------------------------------------------------------------------------

  var TOAST_ID = 'wl-cart-toast';
  var toastHideTimer = null;

  function ensureToastStyles() {
    if (document.getElementById('wl-cart-toast-style')) return;
    var style = document.createElement('style');
    style.id = 'wl-cart-toast-style';
    style.textContent =
      '#' + TOAST_ID + '{position:fixed;left:50%;bottom:24px;transform:translateX(-50%) translateY(12px);' +
      'max-width:min(92vw,420px);background:#1a1a1a;color:#fff;padding:12px 18px;border-radius:6px;' +
      'font-size:14px;line-height:1.4;box-shadow:0 6px 20px rgba(0,0,0,.25);z-index:99999;opacity:0;' +
      'pointer-events:none;transition:opacity .2s ease,transform .2s ease;text-align:center}' +
      '#' + TOAST_ID + '.is-visible{opacity:1;transform:translateX(-50%) translateY(0)}';
    document.head.appendChild(style);
  }

  /**
   * Shows a brief, dismiss-itself notification. Idempotent element (one toast
   * on the page, reused/retimed on a second call) rather than stacking — a
   * rapid double-click on Add to Cart should not queue two toasts.
   */
  function showToast(message, opts) {
    if (!message || typeof document === 'undefined') return;
    ensureToastStyles();
    var duration = (opts && opts.duration) || 4500;
    var el = document.getElementById(TOAST_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = TOAST_ID;
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.remove('is-visible');
    // Force reflow so re-adding the class retriggers the transition even when
    // a toast is already showing (rapid repeated failures).
    void el.offsetWidth;
    el.classList.add('is-visible');
    if (toastHideTimer) clearTimeout(toastHideTimer);
    toastHideTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, duration);
  }

  window.CartBundleAdd = { smartAdd: smartAdd, notifyCartUI: notifyCartUI, showToast: showToast };
})();
